import React, { useState, useRef, useEffect } from "react";
import ClientSideReactTable from "commons/table/ClientSideReactTable";
import DismissableModal from "components/DismissableModal";
import {
  UNPROCESSED_FAX_PROGRAM_TYPE_LIST_COLUMNS,
  UNPROCESSED_FAX_PROGRAM_TYPE_MODAL_COLUMNS,
} from "lookup/UnprocessedFaxListConstantsV2";
import { useNavigate } from "react-router-dom";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import { MdRefresh } from "react-icons/md";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const CACHE_KEY = 'unprocessed_fax_program_type_list_cache';

const UnprocessedFaxProgramTypeList = () => {
    const [unprocessedFaxes, setUnprocessedFaxes] = useState({});
    const [selectedRow, setSelectedRow] = useState(null);
    const [totalFaxes, setTotalFaxes] = useState(0);
    const [loading, setLoading] = useState(false);
    const modalRef = useRef(null);
    const navigate = useNavigate();

    const handlePriorityClick = (row) => {
        setSelectedRow(row.original);
        modalRef.current.handleShow();
    };

    const handleModalRowClick = (row) => {
        const faxProgramCode = selectedRow.faxProgramTypeCode;
        const faxPriority = row.original.priority;

        const query = new URLSearchParams({
            faxProgramCode,
            faxPriority,
        }).toString();
        navigate(`/unprocessed-fax/fax-list?${query}`, { state: { reset: true } });
    };

    const handleFaxProgramRowClick = (row) => {
        if (!row.original.faxCount) {
            showNotification({
                title: "No Faxes Available",
                message: `Fax program type "${row.original.faxProgramTypeName}" has 0 faxes.`,
                position: "tr",
                type: "warning",
            });
            return;
        }

        const faxProgramCode = row.original.faxProgramTypeCode;

        const query = new URLSearchParams({
            faxProgramCode,
        }).toString();

    navigate(`/unprocessed-fax/fax-list?${query}`, { state: { reset: true } });
    };

    const getCachedData = () => {
        try {
            const cached = sessionStorage.getItem(CACHE_KEY);
            if (cached) {
                return JSON.parse(cached);
            }
        } catch (error) {
            console.error('Error reading from sessionStorage:', error);
        }
        return null;
    };

    const setCachedData = (data) => {
        try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
        } catch (error) {
            console.error('Error writing to sessionStorage:', error);
        }
    };

    const onSuccess = (res, shouldCache = true) => {        
        const data = {
            totalFaxCount: res.data.totalFaxCount,
            unprocessedFaxProgramTypeList: res.data.unprocessedFaxProgramTypeList
        };
        
        setTotalFaxes(data.totalFaxCount);
        setUnprocessedFaxes(data.unprocessedFaxProgramTypeList);
        setLoading(false);
        
        if (shouldCache) {
            setCachedData(data);
        }
    }

    const onFailure = () => {
        setLoading(false);
        showNotification({
            title: "Unable to load Unprocessed Faxes!",
            message: "SOMETHING WENT WRONG",
            position: "tr",
            type: "error",
        });
    }

    const fetchData = (forceRefresh = false) => {
        setLoading(true);
        const url = forceRefresh 
            ? `/ufl/program-type-list?forceRefresh=true`
            : `/ufl/program-type-list`;
        sendAuthenticatedAsyncRequest(url, "GET", null, (res) => onSuccess(res, true), onFailure);
    }

    const handleRefresh = () => {
        fetchData(true);
    }

    useEffect(() => {
        // Try to load from cache first
        const cachedData = getCachedData();
        if (cachedData) {
            setTotalFaxes(cachedData.totalFaxCount);
            setUnprocessedFaxes(cachedData.unprocessedFaxProgramTypeList);
            setLoading(false);
        } else {
            // If no cache, fetch from API
            fetchData(false);
        }
    }, [])

    const columns = UNPROCESSED_FAX_PROGRAM_TYPE_LIST_COLUMNS(handlePriorityClick);
    const modalColumns = UNPROCESSED_FAX_PROGRAM_TYPE_MODAL_COLUMNS();

    return (
        <div className="p-4">
            <div className='codex-version-display-bar d-flex justify-content-between align-items-center p-3'>
                <span style={{ float: 'left' }} >Total Unprocessed Faxes: <strong>{totalFaxes}</strong></span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <strong>Unprocessed Fax List</strong>
                    <OverlayTrigger
                        overlay={<Tooltip>Refresh Data</Tooltip>}
                    >
                        <MdRefresh 
                            data-testid="program-type-refresh"
                            onClick={handleRefresh}
                            style={{ cursor: 'pointer', fontSize: '1.2em' }}
                        />
                    </OverlayTrigger>
                </div>
            </div>
            <ClientSideReactTable
                columnDefination={columns}
                data={unprocessedFaxes}
                showPagination={false}
                pageSize={25}
                onRowClick={handleFaxProgramRowClick}
                rowClassName="clickable-row"
                loading={loading}
            />
            <DismissableModal
                ref={modalRef}
                title="Priority Details"
                openModalButton={<></>}
                size="lg"
                centered
                draggable={false}
            >
                {selectedRow && selectedRow.unprocessedFaxPriorities && selectedRow.unprocessedFaxPriorities.length > 0 ? (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <ClientSideReactTable
                            columnDefination={modalColumns}
                            data={selectedRow.unprocessedFaxPriorities}
                            showPagination={false}
                            onRowClick={handleModalRowClick}
                            rowClassName="clickable-row"
                            
                        />
                    </div>
                ) : <p>No priority details available for this program.</p>}
            </DismissableModal>
        </div>
    );
}

export default UnprocessedFaxProgramTypeList;